import { type IErrorData } from '@/utils/getErrorData';
import { createSlice } from '@reduxjs/toolkit';
import {
  getVendorOrderOffersThunk,
  navigateToPageThunk,
  getVendorNamesThunk,
  updateVendorOrderOfferVendorThunk,
  getOffersCountByStatusAndVendorThunk
} from './vendorsThunks';
import { updateVendorOrderOfferStatusSharedThunk } from '../shared/vendorOrderOfferThunks';
import {
  type IVendorOrderItem,
  type IOrderIdInfo
} from '@/types/IVendorOrderResponse';
import { type IVendorItem } from '@/types/IFetchOrderVendorParams';
import { IOffersByStatusResponse } from '@/types/IVendorWithCountOffers';

export interface IVendorOrderGroup {
  orderInfo: IOrderIdInfo;
  items: IVendorOrderItem[];
}

export interface IVendorsState {
  orderGroups: Record<string, IVendorOrderGroup>;
  orderedUuids: string[];
  pagination: {
    currentOffset: number;
    limit: number;
    totalItems: number;
    hasMore: boolean;
  };
  filters: {
    only_wait_action?: boolean;
  };
  isLoading: boolean;
  error: IErrorData | null;
  updatingStatuses: Record<string, boolean>;
  updatingVendors: Record<string, boolean>;
  vendorNames: IVendorItem[];
  isLoadingVendorNames: boolean;
  vendorNamesError: IErrorData | null;
  offersCountByStatus: IOffersByStatusResponse | null;
  isLoadingOffersCount: boolean;
  offersCountError: IErrorData | null;
}

const initialState: IVendorsState = {
  orderGroups: {},
  orderedUuids: [],
  pagination: {
    currentOffset: 0,
    limit: 50,
    totalItems: 0,
    hasMore: true
  },
  filters: {
    only_wait_action: undefined
  },
  isLoading: false,
  error: null,
  updatingStatuses: {},
  updatingVendors: {},
  vendorNames: [],
  isLoadingVendorNames: false,
  vendorNamesError: null,
  offersCountByStatus: null,
  isLoadingOffersCount: false,
  offersCountError: null
};

const vendors = createSlice({
  name: 'vendors',
  initialState,
  reducers: {
    resetVendorOrderOffers(state) {
      state.orderGroups = initialState.orderGroups;
      state.orderedUuids = initialState.orderedUuids;
      state.pagination = { ...initialState.pagination };
      state.isLoading = initialState.isLoading;
      state.error = initialState.error;
    },
    clearVendorError(state) {
      state.error = null;
    },
    updatePaginationOffset(state, action) {
      state.pagination.currentOffset = action.payload;
    },
    forceRefreshCurrentPage(state) {
      state.error = null;
    },
    updateOnlyWaitActionFilter(state, action) {
      state.filters.only_wait_action = action.payload;
      state.pagination.currentOffset = 0;
      state.orderGroups = {};
      state.orderedUuids = [];
    },
    clearVendorNames(state) {
      state.vendorNames = [];
      state.vendorNamesError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getVendorOrderOffersThunk.pending, (state, action) => {
        if (action.meta.arg.silent !== true) {
          state.isLoading = true;
          state.error = null;
        }
      })
      .addCase(getVendorOrderOffersThunk.fulfilled, (state, action) => {
        const { items, total, requestedOffset } = action.payload;

        // Обновляем фильтр из параметров запроса
        state.filters.only_wait_action = action.meta.arg.only_wait_action;

        // Обновляем общую информацию о пагинации
        state.pagination.totalItems = total;
        state.pagination.currentOffset = requestedOffset;
        state.pagination.hasMore = requestedOffset + items.length < total;

        // ОЧИЩАЕМ данные текущей страницы и заполняем новыми
        state.orderGroups = {};
        state.orderedUuids = [];

        // Группируем айтемы по uuid заказа (без сортировки - как приходят с сервера)
        const processedUuids = new Set<string>();

        items.forEach((item) => {
          const orderUuid = item.order_offer.order_id.uuid;

          if (!processedUuids.has(orderUuid)) {
            // Создаем новую группу заказа
            state.orderGroups[orderUuid] = {
              orderInfo: item.order_offer.order_id,
              items: []
            };

            // Добавляем uuid в список В ПОРЯДКЕ ПОЯВЛЕНИЯ
            state.orderedUuids.push(orderUuid);
            processedUuids.add(orderUuid);
          }

          // Добавляем айтем в заказ
          state.orderGroups[orderUuid].items.push(item);
        });

        if (action.meta.arg.silent !== true) {
          state.isLoading = false;
        }
        state.error = null;
      })
      .addCase(getVendorOrderOffersThunk.rejected, (state, action) => {
        if (action.meta.arg.silent !== true) {
          state.isLoading = false;
        }
        state.error = action.payload ?? {
          message: 'Ошибка при получении данных поставщиков для заказов'
        };
      })
      .addCase(navigateToPageThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(navigateToPageThunk.fulfilled, (state, action) => {
        const { items, total, requestedOffset } = action.payload;
        state.filters.only_wait_action = action.meta.arg.only_wait_action;
        state.pagination.currentOffset = requestedOffset;
        state.pagination.totalItems = total;
        state.pagination.hasMore = requestedOffset + items.length < total;
        state.orderGroups = {};
        state.orderedUuids = [];
        if (items.length > 0) {
          const processedUuids = new Set<string>();

          items.forEach((item) => {
            const orderUuid = item.order_offer.order_id.uuid;

            if (!processedUuids.has(orderUuid)) {
              state.orderGroups[orderUuid] = {
                orderInfo: item.order_offer.order_id,
                items: []
              };
              state.orderedUuids.push(orderUuid);
              processedUuids.add(orderUuid);
            }
            state.orderGroups[orderUuid].items.push(item);
          });
        }

        state.isLoading = false;
        state.error = null;
      })
      .addCase(navigateToPageThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? {
          message: 'Ошибка при навигации по страницам'
        };
      })
      // Обработка обновления статуса торгового предложения
      .addCase(
        updateVendorOrderOfferStatusSharedThunk.pending,
        (state, action) => {
          const { order_offer_uuid } = action.meta.arg;
          state.updatingStatuses[order_offer_uuid] = true;
          state.error = null;
        }
      )
      .addCase(
        updateVendorOrderOfferStatusSharedThunk.fulfilled,
        (state, action) => {
          const updatedItem = action.payload;
          const { order_offer_uuid } = action.meta.arg;
          state.updatingStatuses[order_offer_uuid] = false;

          // Проверяем, что обновлённый айтем валиден
          if (
            updatedItem !== undefined &&
            updatedItem !== null &&
            typeof updatedItem.id === 'string' &&
            updatedItem.id !== undefined &&
            updatedItem.id !== null &&
            updatedItem.id !== ''
          ) {
            Object.values(state.orderGroups).forEach((group) => {
              if (group.items.some((item) => item.id === updatedItem.id)) {
                group.items = group.items.map((item) =>
                  item.id === updatedItem.id ? updatedItem : item
                );
              }
            });
          }

          state.error = null;
        }
      )
      .addCase(
        updateVendorOrderOfferStatusSharedThunk.rejected,
        (state, action) => {
          const { order_offer_uuid } = action.meta.arg;
          state.updatingStatuses[order_offer_uuid] = false;
          state.error = action.payload ?? {
            message: 'Ошибка при обновлении статуса торгового предложения'
          };
        }
      )
      .addCase(getVendorNamesThunk.pending, (state) => {
        state.isLoadingVendorNames = true;
        state.vendorNamesError = null;
      })
      .addCase(getVendorNamesThunk.fulfilled, (state, action) => {
        state.vendorNames = action.payload;
        state.isLoadingVendorNames = false;
        state.vendorNamesError = null;
      })
      .addCase(getVendorNamesThunk.rejected, (state, action) => {
        state.isLoadingVendorNames = false;
        state.vendorNamesError = action.payload ?? {
          message: 'Ошибка при получении списка поставщиков'
        };
      })
      // Обработка обновления поставщика торгового предложения
      .addCase(updateVendorOrderOfferVendorThunk.pending, (state, action) => {
        const { order_offer_uuid } = action.meta.arg;
        state.updatingVendors[order_offer_uuid] = true;
        state.error = null;
      })
      .addCase(updateVendorOrderOfferVendorThunk.fulfilled, (state, action) => {
        const updatedItem = action.payload;
        const { order_offer_uuid } = action.meta.arg;
        state.updatingVendors[order_offer_uuid] = false;

        // Проверяем, что обновлённый айтем валиден
        if (
          updatedItem !== undefined &&
          updatedItem !== null &&
          typeof updatedItem.id === 'string' &&
          updatedItem.id !== undefined &&
          updatedItem.id !== null &&
          updatedItem.id !== ''
        ) {
          Object.values(state.orderGroups).forEach((group) => {
            if (group.items.some((item) => item.id === updatedItem.id)) {
              group.items = group.items.map((item) => {
                if (item.id === updatedItem.id) {
                  // Мержим данные: сохраняем старые поля offer, если новые не пришли
                  return {
                    ...item,
                    ...updatedItem,
                    order_offer: {
                      ...item.order_offer,
                      ...updatedItem.order_offer,
                      offer: {
                        ...item.order_offer.offer,
                        ...updatedItem.order_offer?.offer
                      },
                      order_id: {
                        ...item.order_offer.order_id,
                        ...updatedItem.order_offer?.order_id
                      }
                    }
                  };
                }
                return item;
              });
            }
          });
        }

        state.error = null;
      })
      .addCase(updateVendorOrderOfferVendorThunk.rejected, (state, action) => {
        const { order_offer_uuid } = action.meta.arg;
        state.updatingVendors[order_offer_uuid] = false;
        state.error = action.payload ?? {
          message: 'Ошибка при обновлении поставщика торгового предложения'
        };
      })
      .addCase(getOffersCountByStatusAndVendorThunk.pending, (state) => {
        state.isLoadingOffersCount = true;
        state.offersCountError = null;
      })
      .addCase(
        getOffersCountByStatusAndVendorThunk.fulfilled,
        (state, action) => {
          state.offersCountByStatus = action.payload;
          state.isLoadingOffersCount = false;
          state.offersCountError = null;
        }
      )
      .addCase(
        getOffersCountByStatusAndVendorThunk.rejected,
        (state, action) => {
          state.isLoadingOffersCount = false;
          state.offersCountError = action.payload ?? {
            message: 'Ошибка при получении количества офферов по статусу'
          };
        }
      );
  }
});

export const {
  resetVendorOrderOffers,
  clearVendorError,
  updatePaginationOffset,
  forceRefreshCurrentPage,
  updateOnlyWaitActionFilter,
  clearVendorNames
} = vendors.actions;
export default vendors.reducer;

export { useVendorOrderOffers } from '../../hooks/useVendorOrderOffers';
